import { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { SP_BREAKPOINT } from "../../../constants/app";
import { FileTypeNumber } from "../../../core/api/requests/file/download/types";
import { TableHeaderItem } from "../../../hooks/useTable";
import { Button } from "../../atoms/Button";
import { Checkbox } from "../../atoms/Checkbox";
import { DeleteIcon } from "../../atoms/icons/DeleteIcon";
import { InvalidIcon } from "../../atoms/icons/InvalidIcon";

type Props<DataType> = {
  headers: TableHeaderItem<DataType>[];
  data: DataType;
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  downloadable?: boolean;
  enableCSVDownload?: boolean;
  selected?: boolean;
  enableHoverPopup?: boolean;
  onEdit?: (data: DataType) => void;
  onDelete?: (data: DataType) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
  onDownload?: (data: DataType, fileType: FileTypeNumber) => void;
  onCSVDownload?: (data: DataType, fileType: FileTypeNumber) => void;
};

export const TableRow = <
  DataType extends {
    selected?: boolean;
    disabled?: boolean;
    disabledDownload?: boolean;
    disabledDownloadCSV?: boolean;
    invalid?: boolean;
  },
>({
  headers,
  data,
  selectable,
  editable,
  onEdit,
  onSelect,
  onDelete,
  onDownload,
  onCSVDownload,
  deletable,
  downloadable,
  enableCSVDownload,
  enableHoverPopup,
}: Props<DataType>) => {
  const [isHovered, setIsHovered] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    showAbove: false,
  });

  const handleMouseEnter = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (!enableHoverPopup) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const popupHeight = 40 * headers.length + 32;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < popupHeight;

    setPopupPosition({
      top: showAbove
        ? rect.top + window.scrollY - popupHeight - 10
        : rect.bottom + window.scrollY + 10,
      left: window.innerWidth / 2,
      showAbove,
    });

    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (enableHoverPopup) {
      setIsHovered(false);
    }
  };

  return (
    <>
      <StyledRowWrapper
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $isHovered={isHovered}
        $invalid={data.invalid}
      >
        {selectable && (
          <td style={{ width: "24px" }}>
            <Checkbox
              name=""
              variant="empty"
              checked={data.selected}
              onChange={(checked) => onSelect?.(data, checked)}
              disabled={data.disabled}
            />
          </td>
        )}
        {headers.map((header, i) => {
          const rowData = header.dataMapFunction
            ? header.dataMapFunction(data)
            : (data as any)[header.dataKey];

          return (
            <StyledTableData
              key={`table-data-${i}`}
              $width={header.width || "200px"}
              $invalid={data.invalid}
              $i={i}
            >
              {header.customComponent ? (
                <header.customComponent {...rowData} />
              ) : (
                <span>{rowData}</span>
              )}
              {data.invalid && i === 0 && (
                <StyledIcon>
                  <InvalidIcon />
                </StyledIcon>
              )}
            </StyledTableData>
          );
        })}
        {editable && (
          <td style={{ width: "100px" }}>
            <Button text="編集" variant="dark" onClick={() => onEdit?.(data)} />
          </td>
        )}

        {(deletable || downloadable) && (
          <td
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              width: "350px",
            }}
          >
            {downloadable && (
              <Button
                text="ダウンロード"
                variant="dark"
                onClick={() => onDownload?.(data, FileTypeNumber.EXCEL)}
                disabled={data.disabledDownload}
              />
            )}
            {enableCSVDownload && (
              <Button
                text="CSVダウンロード"
                variant="dark"
                onClick={() => onCSVDownload?.(data, FileTypeNumber.CSV)}
                disabled={data.disabledDownloadCSV}
              />
            )}
            {deletable && (
              <StyledDeleteButton
                $disabled={data.disabled}
                onClick={() => !data.disabled && onDelete?.(data)}
              >
                <DeleteIcon />
              </StyledDeleteButton>
            )}
          </td>
        )}
      </StyledRowWrapper>

      {enableHoverPopup &&
        isHovered &&
        createPortal(
          <StyledPopupContainer
            style={{ top: popupPosition.top, left: popupPosition.left }}
            $showAbove={popupPosition.showAbove}
          >
            {headers.map((header, index) => {
              const value = header.dataMapFunctionForPopup
                ? header.dataMapFunctionForPopup(data)
                : header.dataMapFunction
                  ? header.dataMapFunction(data)
                  : // @ts-expect-error
                    data[header.dataKey];

              return (
                <StyledPopupContent key={index}>
                  <StyledPopupLabel>
                    {header.popupTitle ?? header.title}
                  </StyledPopupLabel>
                  <StyledPopupValue>
                    {typeof value === "string" ? value : value.text}
                  </StyledPopupValue>
                </StyledPopupContent>
              );
            })}
          </StyledPopupContainer>,
          document.body,
        )}
    </>
  );
};

const StyledRowWrapper = styled.tr<{ $isHovered: boolean; $invalid?: boolean }>`
  background-color: ${({ $invalid }) => ($invalid ? "#FBFBFB" : "white")};
  transition:
    background 0.2s ease-in-out,
    transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  ${({ $isHovered }) =>
    $isHovered &&
    `
      transform: scale(1.02);
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08); 
  `}
`;

const StyledPopupContainer = styled.div<{ $showAbove: boolean }>`
  position: fixed;
  background-color: #ffffff;
  padding: 16px;
  border-radius: 10px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  max-width: 380px;
  font-size: 14px;
  color: #333;
  z-index: 100;
  transition: opacity 0.2s ease-in-out;
  transform: translateX(-50%);
  left: 50%;

  &:before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-width: 12px;
    border-style: solid;
    border-color: ${({ $showAbove }) =>
      $showAbove
        ? "white transparent transparent transparent"
        : "transparent transparent white transparent"};
    top: ${({ $showAbove }) => ($showAbove ? "100%" : "unset")};
    bottom: ${({ $showAbove }) => ($showAbove ? "unset" : "100%")};
  }

  @media (max-width: ${SP_BREAKPOINT}) {
    width: calc(100vw - 20px);
  }
`;

const StyledPopupContent = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  row-gap: 8px;
`;

const StyledPopupLabel = styled.div`
  font-weight: 500;
  color: #666;
  line-height: 2.5;
`;

const StyledPopupValue = styled.div`
  font-weight: 400;
  color: #333;
  line-height: 2.5;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const StyledDeleteButton = styled.button<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const StyledTableData = styled.td<{
  $width?: string;
  $invalid?: boolean;
  $i: number;
}>`
  width: ${({ $width }) => $width || "200px"};
  max-width: ${({ $width }) => $width || "200px"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  word-wrap: break-word;
  ${({ $invalid, $i }) =>
    $invalid &&
    $i === 0 &&
    `
      display: flex;
      align-items: center;
      `}
`;

const StyledIcon = styled.div`
  padding: 11px 0 7px 2px;
`;
